import { API_URLS } from '@helpers/api';
import useFocusWithin from '@hooks/useFocusWithin';
import { LinkButton } from '@stories/Components/Buttons/GeneralButton/Button';
import { AnimatePresence } from 'framer-motion';
import * as React from 'react';
import S from './SearchField.styles';
const SearchField = ({ popularSearchTerms = [], searchResultsPlaceholder, searchResultsUrl, showSearch, visibleCount = 9, onClose, }) => {
    const [searchTerm, setSearchTerm] = React.useState('');
    const items = useApi(searchTerm);
    const refContainer = React.useRef(null);
    const refResultsContainer = React.useRef(null);
    const refInput = React.useRef(null);
    const showResults = useFocusWithin(refContainer);
    React.useEffect(() => {
        if (showSearch) {
            refInput.current?.focus();
        }
    }, [showSearch]);
    return (React.createElement(S.Container, { ref: refContainer, active: showSearch, showResults: showResults && !!items.length, onKeyDown: (event) => {
            if (event.key.toLowerCase() === 'arrowdown') {
                handleArrowDown(event);
            }
            if (event.key.toLowerCase() === 'arrowup') {
                handleArrowUp(event);
            }
            if (event.key.toLowerCase() === 'escape') {
                handleEscape(event);
            }
            if (event.key.toLowerCase() === 'enter') {
                handleRedirect(event);
            }
        } },
        React.createElement(S.Inner, null,
            React.createElement(S.SearchContainer, null,
                React.createElement(S.Input, { ref: refInput, placeholder: searchResultsPlaceholder, "aria-label": searchResultsPlaceholder, role: "searchbox", type: "search", name: "search", value: searchTerm, onChange: (event) => {
                        setSearchTerm(event.target.value);
                    } }),
                React.createElement(S.Submit, { type: "submit", onClick: handleRedirect, role: "search", "aria-label": "Search" },
                    React.createElement(S.SubmitLabel, null, "Search"),
                    React.createElement(S.SubmitIcon, null))),
            React.createElement(S.Close, { type: "button", "aria-label": "Close search", onClick: (event) => {
                    event.preventDefault();
                    onClose();
                } },
                React.createElement(S.CloseLabel, null, "Close"),
                React.createElement(S.CloseIcon, null)),
            React.createElement(AnimatePresence, null, showResults && !!items?.length && (React.createElement(S.ResultsContainer, { ref: refResultsContainer, initial: 'hidden', animate: 'visible', exit: 'hidden', variants: {
                    hidden: {
                        opacity: 0,
                        transition: { type: 'tween', ease: [0.3, 0, 0.7, 1] },
                    },
                    visible: {
                        opacity: 1,
                        transition: { type: 'tween', ease: [0.3, 0, 0.7, 1] },
                    },
                } }, items?.slice(0, visibleCount).map((item) => (React.createElement(S.ResultItem, { key: item.url, href: item.url, tabIndex: -1 },
                React.createElement(S.ResultItemText, null, item.value)))))))),
        React.createElement(S.TermListContainer, null,
            React.createElement(S.TermListTitle, null, "Popular search terms"),
            React.createElement(S.TermList, null, popularSearchTerms.map((term) => (React.createElement(S.TermItem, { key: term.id }, term.redirectUrl ?
                React.createElement(LinkButton, { branding: "secondary", href: `${term.redirectUrl}`, title: term.value })
                :
                    React.createElement(LinkButton, { branding: "secondary", href: `${searchResultsUrl}?search=${term.value}`, title: term.value }))))))));
    function handleRedirect(event) {
        event.preventDefault();
        if (!searchTerm || searchTerm.length === 0 || searchResultsUrl === null) {
            return;
        }
        window.location.href = `${searchResultsUrl}?search=${searchTerm}`;
    }
    function handleArrowDown(event) {
        event.preventDefault();
        if (showResults) {
            const resultSelected = refResultsContainer.current?.contains(document.activeElement);
            if (resultSelected) {
                const nextResult = document.activeElement?.nextElementSibling;
                if (nextResult) {
                    nextResult.focus();
                }
            }
            else {
                const firstResult = refResultsContainer.current?.firstChild;
                if (firstResult) {
                    firstResult.focus();
                }
            }
        }
    }
    function handleArrowUp(event) {
        event.preventDefault();
        if (showResults) {
            const resultSelected = refResultsContainer.current?.contains(document.activeElement);
            if (resultSelected) {
                const previousResult = document.activeElement?.previousElementSibling;
                if (previousResult) {
                    previousResult.focus();
                }
            }
        }
    }
    function handleEscape(event) {
        event.preventDefault();
        if (showResults) {
            const resultSelected = refResultsContainer.current?.contains(document.activeElement);
            if (resultSelected) {
                refInput.current?.focus();
            }
        }
    }
};
export default SearchField;
/** Use typeahead results for search term. */
function useApi(searchTerm) {
    const [data, setData] = React.useState([]);
    React.useEffect(() => {
        if (!searchTerm || searchTerm.length < 3) {
            return setData([]);
        }
        // Check for AbortController support (ie11).
        const abortController = window.AbortController ? new AbortController() : undefined;
        fetchApi(searchTerm, abortController?.signal).then((data) => setData(data));
        return function cancel() {
            abortController?.abort();
        };
    }, [searchTerm]);
    return data;
}
/** Call typeahead API endpoint. */
async function fetchApi(searchTerm, signal) {
    const res = await fetch(`${API_URLS.typeahead}?query=${searchTerm}`, { signal });
    if (!res.ok) {
        return [];
    }
    const data = await res.json();
    if (!data) {
        return [];
    }
    return data;
}
